import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { 
  Database, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Wrench,
  Activity
} from 'lucide-react'
import { DatabaseDiagnostic } from '@/components/admin/DatabaseDiagnostic'
import { runBlogDiagnostic, quickFixBlogIssues } from '@/utils/blogDiagnostic'
import { toast } from 'sonner'

interface DiagnosticResult {
  test: string
  success: boolean
  message: string
  details?: unknown
  fix?: string
}

const DatabaseSetupPage = () => {
  const [isRunningDiagnostic, setIsRunningDiagnostic] = useState(false)
  const [isRunningFix, setIsRunningFix] = useState(false)
  const [diagnosticResults, setDiagnosticResults] = useState<DiagnosticResult[]>([])
  const [fixResults, setFixResults] = useState<DiagnosticResult[]>([])
  const [diagnosticSummary, setDiagnosticSummary] = useState<string>('')

  const handleRunDiagnostic = async () => {
    setIsRunningDiagnostic(true)
    setDiagnosticResults([])
    
    try {
      console.log('🔍 Lancement du diagnostic complet...')
      const result = await runBlogDiagnostic()
      
      setDiagnosticResults(result.results)
      setDiagnosticSummary(result.summary)
      
      if (result.success) {
        toast.success('Diagnostic terminé - Système fonctionnel ✅')
      } else {
        toast.warning('Diagnostic terminé - Problèmes détectés ⚠️')
      }
    } catch (error) {
      console.error('Erreur lors du diagnostic:', error)
      toast.error('Erreur lors du diagnostic')
    } finally {
      setIsRunningDiagnostic(false)
    }
  }

  const handleQuickFix = async () => {
    setIsRunningFix(true)
    setFixResults([])
    
    try {
      console.log('🔧 Lancement de la correction rapide...')
      const result = await quickFixBlogIssues()
      
      setFixResults(result.results)
      
      if (result.success) {
        toast.success('Correction terminée ✅')
        // Relancer le diagnostic après la correction
        setTimeout(() => {
          handleRunDiagnostic()
        }, 1000)
      } else {
        toast.error('Erreur lors de la correction')
      }
    } catch (error) {
      console.error('Erreur lors de la correction:', error)
      toast.error('Erreur lors de la correction')
    } finally {
      setIsRunningFix(false)
    }
  }

  const renderDiagnosticResult = (result: DiagnosticResult, index: number) => (
    <div key={index} className="border rounded-lg p-4">
      <div className="flex items-center gap-3 mb-2">
        {result.success ? (
          <CheckCircle className="w-5 h-5 text-green-500" />
        ) : (
          <XCircle className="w-5 h-5 text-red-500" />
        )}
        <h4 className="font-medium">{result.test}</h4>
      </div>
      
      <p className="text-sm text-gray-600 mb-2">{result.message}</p>
      
      {result.fix && (
        <Alert className="mt-2">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-sm">
            <strong>Solution :</strong> {result.fix}
          </AlertDescription>
        </Alert>
      )}
      
      {result.details && (
        <details className="mt-2">
          <summary className="text-xs text-gray-500 cursor-pointer">Détails techniques</summary>
          <pre className="text-xs bg-gray-100 p-2 rounded mt-1 overflow-auto">
            {JSON.stringify(result.details, null, 2)}
          </pre>
        </details>
      )}
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Configuration Base de Données
          </h1>
          <p className="text-gray-600">
            Diagnostic et correction des problèmes de base de données pour le système de blog
          </p>
        </div>

        {/* Actions principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Diagnostic Rapide
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Lance un diagnostic complet du système de blog pour identifier les problèmes
              </p>
              <Button 
                onClick={handleRunDiagnostic}
                disabled={isRunningDiagnostic}
                className="w-full"
              >
                {isRunningDiagnostic ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Diagnostic en cours...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Lancer le diagnostic
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Correction Automatique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Corrige automatiquement les problèmes courants (bucket, permissions admin)
              </p>
              <Button 
                onClick={handleQuickFix}
                disabled={isRunningFix}
                className="w-full"
                variant="outline"
              >
                {isRunningFix ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Correction en cours...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Correction rapide
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Résumé du diagnostic */}
        {diagnosticSummary && (
          <Alert className="mb-6">
            <Activity className="w-4 h-4" />
            <AlertDescription>
              {diagnosticSummary}
            </AlertDescription>
          </Alert>
        )}

        {/* Résultats du diagnostic */}
        {diagnosticResults.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Résultats du Diagnostic</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {diagnosticResults.map((result, index) => renderDiagnosticResult(result, index))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Résultats de la correction */}
        {fixResults.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Résultats de la Correction</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {fixResults.map((result, index) => renderDiagnosticResult(result, index))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Diagnostic avancé */}
        <Card>
          <CardHeader>
            <CardTitle>Diagnostic Avancé</CardTitle>
          </CardHeader>
          <CardContent>
            <DatabaseDiagnostic />
          </CardContent>
        </Card>

        {/* Instructions manuelles */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Instructions Manuelles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Si les corrections automatiques échouent :</h4>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-600">
                  <li>Connectez-vous au dashboard Supabase</li>
                  <li>Allez dans l'éditeur SQL</li>
                  <li>Exécutez le script <code>supabase/migrations/20240401000000_create_blog_system.sql</code></li>
                  <li>Ou utilisez le script de correction <code>fix-rls.sql</code></li>
                </ol>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Problèmes courants :</h4>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li><strong>Erreur RLS :</strong> Politiques de sécurité trop restrictives</li>
                  <li><strong>Bucket manquant :</strong> Storage blog-images non configuré</li>
                  <li><strong>Permissions admin :</strong> Utilisateur non ajouté à admin_users</li>
                  <li><strong>Types de données :</strong> Incompatibilité dans le schéma</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default DatabaseSetupPage 